import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useAppDispatch} from 'hooks';
import {
  fetchBehavioralIndicatorsThunk,
  fetchCharacterTraitsThunk,
  fetchCompetenciesThunk,
  fetchEthnicitiesThunk,
  fetchGendersThunk,
  fetchImageSourcesThunk,
  fetchLanguagesThunk,
  fetchPillarsThunk,
  fetchProgramsThunk,
  fetchPublishedOutputToolsThunk,
  fetchResourcesThunk,
  fetchTagGradeLevelsThunk,
  filtersBehavioralIndicatorsEntitiesCollectionSelector,
  filtersCharacterTraitsEntitiesCollectionSelector,
  filtersCompetenciesEntitiesCollectionSelector,
  filtersEthnicitiesEntitiesCollectionSelector,
  filtersGendersEntitiesCollectionSelector,
  filtersImageSourcesEntitiesCollectionSelector,
  filtersLanguagesEntitiesCollectionSelector,
  filtersPublishedOutputToolsEntitiesCollectionSelector,
  filtersTagGradeLevelsEntitiesCollectionSelector,
  pillarsEntitiesCollectionSelector,
  programsEntitiesCollectionSelector,
  resourcesEntitiesCollectionSelector,
} from 'filters-selections/store';

/**
 * Prefetch static metadata lookups once when a resource upload/edit page mounts.
 * Selects only read from Redux afterward (including image-only fields that mount later).
 */
export const usePrefetchResourceMetadataLookups = () => {
  const dispatch = useAppDispatch();
  const hasPrefetchedRef = useRef(false);

  const languages = useSelector(filtersLanguagesEntitiesCollectionSelector);
  const programs = useSelector(programsEntitiesCollectionSelector);
  const tagGradeLevels = useSelector(filtersTagGradeLevelsEntitiesCollectionSelector);
  const pillars = useSelector(pillarsEntitiesCollectionSelector);
  const competencies = useSelector(filtersCompetenciesEntitiesCollectionSelector);
  const characterTraits = useSelector(filtersCharacterTraitsEntitiesCollectionSelector);
  const behavioralIndicators = useSelector(filtersBehavioralIndicatorsEntitiesCollectionSelector);
  const imageSources = useSelector(filtersImageSourcesEntitiesCollectionSelector);
  const publishedOutputTools = useSelector(filtersPublishedOutputToolsEntitiesCollectionSelector);
  const genders = useSelector(filtersGendersEntitiesCollectionSelector);
  const ethnicities = useSelector(filtersEthnicitiesEntitiesCollectionSelector);
  const resources = useSelector(resourcesEntitiesCollectionSelector);

  useEffect(() => {
    if (hasPrefetchedRef.current) {
      return;
    }
    hasPrefetchedRef.current = true;

    if (!languages.length) {
      dispatch(fetchLanguagesThunk());
    }
    if (!programs.length) {
      dispatch(fetchProgramsThunk());
    }
    if (!tagGradeLevels.length) {
      dispatch(fetchTagGradeLevelsThunk());
    }
    if (!pillars.length) {
      dispatch(fetchPillarsThunk());
    }
    if (!competencies.length) {
      dispatch(fetchCompetenciesThunk());
    }
    if (!characterTraits.length) {
      dispatch(fetchCharacterTraitsThunk());
    }
    if (!behavioralIndicators.length) {
      dispatch(fetchBehavioralIndicatorsThunk());
    }
    if (!imageSources.length) {
      dispatch(fetchImageSourcesThunk());
    }
    if (!publishedOutputTools.length) {
      dispatch(fetchPublishedOutputToolsThunk());
    }
    if (!genders.length) {
      dispatch(fetchGendersThunk());
    }
    if (!ethnicities.length) {
      dispatch(fetchEthnicitiesThunk());
    }
    if (!resources.length) {
      dispatch(
        fetchResourcesThunk({
          filter: {
            query: '',
          },
        })
      );
    }
    // Intentionally run once on mount; Redux cache skips already-loaded lookups.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
};
